import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { getUser } from 'Utils/UserCookie';
import { filterByEventId, initPaymentIframe } from 'Utils/Payment';
import { attachEventListener, removeEventListener } from 'Utils/Browser';
import { trackFormError } from 'Utils/Analytics';
import { updateTaxByZip } from 'State/Checkout/CheckoutActions';

import './PaymentFrame.css';
import { logError } from 'Utils/Logger';
import { getDefaultConfig } from 'Utils/Store';
import { useDispatch, useSelector } from 'react-redux';

// payment iframe events names
const PAYMENT_FORM_FIELD_CHANGE = 'PAYMENT_FORM_FIELD_CHANGE';
const TAX_UPDATED = 'TAX_UPDATED';
const VALID_PAYMENT_FORM = 'VALID_PAYMENT_FORM';
const ONFOCUS_DATA = 'ONFOCUS_DATA';

export const PaymentFrame = ({
  onValidate,
  formName,
  onExpiryError,
  resetFormError,
  setErrorMessage,
  updatePaymentServiceId,
}) => {
  const dispatch = useDispatch();
  const { isPlanSelected, selectedPlan } = useSelector((s) => s.plans);
  const { activeSubscriptionId } = useSelector((s) => s.subscription);
  const user = useSelector((s) => s.user);
  const { profileId } = user;
  const { showCVV, rootDomain, evPaymentFrameUrl } = getDefaultConfig();
  const [, domainName] = rootDomain.split('https://');
  const isCvvRequired = showCVV ? 1 : 0;

  const [accessToken, setAccessToken] = useState('');

  const paymentFrameEventListener = useCallback((eventData) => {
    const { eventId, data } = filterByEventId(eventData);

    if (eventId && eventId === ONFOCUS_DATA) {
      // When the form is re-focused to fix errors, clear error. NATCOM-465
      resetFormError();
      if (setErrorMessage) {
        setErrorMessage('');
      }
    }

    if (eventId) {
      if (eventId === PAYMENT_FORM_FIELD_CHANGE) {
        if (!data.isValid) {
          const { errorDescription } = data;
          trackFormError(formName, {
            message: errorDescription,
            status: 1000,
          });
        }

        if (data.fieldName === 'expiryDate') {
          onExpiryError(!data.isValid);
        }
      }

      if (eventId === VALID_PAYMENT_FORM && onValidate) {
        onValidate(data.isValid);
      }

      if (eventId === TAX_UPDATED) {
        dispatch(updateTaxByZip(data));
      }

      if ((!data.isValid && data.errorType) || data.GetProrateWithTaxResponseMessage?.failureMessage) {
        logError(
          new Error(data.errorType || data.GetProrateWithTaxResponseMessage?.failureMessage?.errorMessage),
          'payment',
          { eventId, ...data }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { accessToken } = getUser();
    setAccessToken(accessToken);

    return () => {
      removeEventListener('message', paymentFrameEventListener);
      console.debug(`Payment Frame: unmount`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accessToken) {
      attachEventListener('message', paymentFrameEventListener);
      initPaymentIframe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const createFrame = () => ({
    __html: `<iframe class="EvPaymentFrame" title="Payment Form" name="paymentpage" frameborder="0" scrolling="no" />`,
  });

  let selectedPlanId = '';
  let promotionId = '';
  if (isPlanSelected && selectedPlan) {
    const { planId, promotionID } = selectedPlan;
    selectedPlanId = planId;
    promotionId = promotionID;
  }
  const serviceId = updatePaymentServiceId || selectedPlanId || activeSubscriptionId;

  return (
    <Fragment>
      <form id="evPaymentIframe" target="paymentpage" action={evPaymentFrameUrl} method="post">
        <input type="hidden" name="serviceId" value={serviceId} />
        <input type="hidden" name="isCvvRequired" value={isCvvRequired} />
        <input type="hidden" name="domainName" value={domainName} />
        <input type="hidden" name="cpCustomerID" value={profileId} />
        <input type="hidden" name="accessToken" value={accessToken} />
        <input type="hidden" name="promotionId" value={promotionId} />
      </form>
      <div dangerouslySetInnerHTML={createFrame()} className="EvPaymentFrameWrapper" />
    </Fragment>
  );
};
